import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { styled } from '@mui/system'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useProjectRulesForm } from './hooks/useProjectRulesForm'
import { colorPalette } from '../../style/colorPalette'
import { Button } from '../../components/buttons'
import { RadioButton } from '../../components/radio-button'
import { HeaderAction } from '../../components/header-action'
import { Title, Text, MultilineText } from '../../components/typography'
import Loading from '../../components/process-state-notifications/Loading'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { openWbsItemSearch } from '../WbsItemSearch/wbsItemSearchOptions'
import { useProjectPrivateContext } from '../../context/projectContext'
import { projectPrivate } from '../../higher-order-components/projectPrivate'

const PageContainer = styled('div')({
  height: '100%',
  overflow: 'auto',
  backgroundColor: colorPalette.monotone[1],
})

const ContentWrapper = styled('div')({
  maxWidth: 800,
  margin: '0 auto',
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
})

const BackButtonArea = styled('div')({})

const SettingsCard = styled('div')({
  backgroundColor: colorPalette.monotone[0],
  borderRadius: 8,
  overflow: 'hidden',
  border: `1px solid ${colorPalette.monotone[2]}`,
})

const CardHeader = styled('div')({
  padding: '16px 24px',
  backgroundColor: colorPalette.monotone[1],
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

const CardTitleDescription = styled('div')({})

const SettingItem = styled('div')({
  padding: '16px 24px',
})

const SettingDescriptionWrapper = styled('div')({
  marginTop: 4,
  marginBottom: 16,
})

const RadioGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
})

const SelectableCard = styled('div', {
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'disabled',
})<{ selected: boolean; disabled?: boolean }>(({ selected, disabled }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '14px 16px',
  borderRadius: 8,
  border: `1.5px solid ${
    disabled
      ? colorPalette.monotone[2]
      : selected
        ? colorPalette.skyBlue[7]
        : colorPalette.monotone[2]
  }`,
  backgroundColor: disabled
    ? colorPalette.monotone[1]
    : selected
      ? colorPalette.skyBlue[0]
      : colorPalette.monotone[0],
  cursor: disabled ? 'default' : 'pointer',
  transition:
    'border-color 0.15s ease-in-out, background-color 0.15s ease-in-out',
  ...(!disabled && {
    '&:hover': {
      borderColor: selected
        ? colorPalette.skyBlue[7]
        : colorPalette.monotone[3],
    },
  }),
  ...(disabled && {
    '& *': {
      color: colorPalette.monotone[3],
    },
  }),
}))

const RadioTextWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})

const ConflictAlert = styled('div')({
  marginTop: 8,
  padding: '10px 12px',
  borderRadius: 6,
  backgroundColor: colorPalette.yellow[0],
  border: `1px solid ${colorPalette.yellow[1]}`,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
})

const InlineLink = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  color: colorPalette.skyBlue[8],
  cursor: 'pointer',
  fontSize: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
})

export const ProjectRules = projectPrivate(() => {
  const history = useHistory()
  const intl = useIntl()
  const { projectUuid } = useProjectPrivateContext()
  const {
    rules,
    updateSprintAssociationMode,
    edited,
    isSaving,
    isLoading,
    save,
    canSwitchToSingle,
    conflictingCodes,
  } = useProjectRulesForm()
  const { sprintTaskAssociationMode } = rules ?? {}

  const canSave = edited && !isSaving

  const onBack = useCallback(() => {
    history.goBack()
  }, [history])

  const onSave = useCallback(async () => {
    if (!canSave) return
    await save()
  }, [save, canSave])

  useKeyBind(
    [
      {
        key: KEY_SAVE,
        fn: onSave,
        stopDefaultBehavior: true,
      },
    ],
    [onSave]
  )

  const handleOpenWbsItemSearch = useCallback(() => {
    if (!projectUuid || conflictingCodes.length === 0) return
    openWbsItemSearch(projectUuid, {
      code: conflictingCodes.join(','),
    })
  }, [projectUuid, conflictingCodes])

  return (
    <PageContainer>
      <Loading isLoading={isSaving || isLoading} />
      <HeaderAction
        title={intl.formatMessage({ id: 'unsavedChanges' })}
        buttonLabel={intl.formatMessage({ id: 'save' })}
        buttonSubLabel={intl.formatMessage({ id: 'saveShortcut' })}
        onButtonClick={onSave}
        open={edited && !isSaving}
      />
      <ContentWrapper>
        <BackButtonArea>
          <Button
            onClick={onBack}
            colorPattern="monotone"
            variant="outlined"
            size="m"
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            {intl.formatMessage({ id: 'back' })}
          </Button>
        </BackButtonArea>

        <SettingsCard>
          <CardHeader>
            <Title variant="l" as="h2">
              {intl.formatMessage({ id: 'projectRules.card.title' })}
            </Title>
            <CardTitleDescription>
              <MultilineText color={colorPalette.monotone[4]}>
                {intl.formatMessage({ id: 'projectRules.card.description' })}
              </MultilineText>
            </CardTitleDescription>
          </CardHeader>

          <SettingItem>
            <Title variant="s" as="h3">
              {intl.formatMessage({
                id: 'projectRules.sprintAssociation.name',
              })}
            </Title>
            <SettingDescriptionWrapper>
              <MultilineText color={colorPalette.monotone[4]}>
                {intl.formatMessage({
                  id: 'projectRules.sprintAssociation.description',
                })}
              </MultilineText>
            </SettingDescriptionWrapper>
            <RadioGroup>
              <SelectableCard
                selected={sprintTaskAssociationMode === 'MULTIPLE'}
                onClick={() => updateSprintAssociationMode('MULTIPLE')}
              >
                <RadioButton
                  name="sprintTaskAssociationMode"
                  checked={sprintTaskAssociationMode === 'MULTIPLE'}
                  onChange={() => updateSprintAssociationMode('MULTIPLE')}
                />
                <RadioTextWrapper>
                  <Text>
                    {intl.formatMessage({
                      id: 'projectRules.sprintAssociation.multiple.name',
                    })}
                  </Text>
                  <Text variant="2s" color={colorPalette.monotone[4]}>
                    {intl.formatMessage({
                      id: 'projectRules.sprintAssociation.multiple.description',
                    })}
                  </Text>
                </RadioTextWrapper>
              </SelectableCard>
              <SelectableCard
                selected={sprintTaskAssociationMode === 'SINGLE'}
                disabled={!canSwitchToSingle}
                onClick={() =>
                  canSwitchToSingle && updateSprintAssociationMode('SINGLE')
                }
              >
                <RadioButton
                  name="sprintTaskAssociationMode"
                  checked={sprintTaskAssociationMode === 'SINGLE'}
                  onChange={() =>
                    canSwitchToSingle && updateSprintAssociationMode('SINGLE')
                  }
                  disabled={!canSwitchToSingle}
                />
                <RadioTextWrapper>
                  <Text>
                    {intl.formatMessage({
                      id: 'projectRules.sprintAssociation.single.name',
                    })}
                  </Text>
                  <Text variant="2s" color={colorPalette.monotone[4]}>
                    {intl.formatMessage({
                      id: 'projectRules.sprintAssociation.single.description',
                    })}
                  </Text>
                  {!canSwitchToSingle && (
                    <ConflictAlert>
                      <Text variant="2s" color={colorPalette.yellow[5]}>
                        {intl.formatMessage({
                          id: 'projectRules.sprintAssociation.conflict.message',
                        })}
                      </Text>
                      <InlineLink onClick={handleOpenWbsItemSearch}>
                        <Text variant="2s" color={colorPalette.skyBlue[8]}>
                          {intl.formatMessage({
                            id: 'projectRules.sprintAssociation.conflict.openSearch',
                          })}
                        </Text>
                        <OpenInNewIcon sx={{ fontSize: 13 }} />
                      </InlineLink>
                    </ConflictAlert>
                  )}
                </RadioTextWrapper>
              </SelectableCard>
            </RadioGroup>
          </SettingItem>
        </SettingsCard>
      </ContentWrapper>
    </PageContainer>
  )
})
